
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MessageSquarePlus, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Board {
  id: string;
  name: string;
  emoji: string;
}

interface FeedbackTabProps {
  companyId: string;
  visitorName: string;
  visitorEmail: string;
  primaryColor?: string;
  textColor?: string;
}

const FeedbackTab = ({
  companyId,
  visitorName,
  visitorEmail,
  primaryColor,
  textColor
}: FeedbackTabProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [boards, setBoards] = useState<Board[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingBoards, setIsLoadingBoards] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    fetchBoards();
  }, [companyId]);

  const fetchBoards = async () => {
    try {
      setIsLoadingBoards(true);
      const { data: boards, error } = await supabase
        .from("feedback_boards")
        .select("id, name, emoji")
        .eq("company_id", companyId);

      if (error) throw error;

      setBoards(boards || []);
      // Set the first board as default
      if (boards && boards.length > 0) {
        setSelectedBoardId(boards[0].id);
      }
    } catch (error) {
      console.error("Error fetching boards:", error);
      toast("Failed to load feedback boards");
    } finally {
      setIsLoadingBoards(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      toast("Please fill in all required fields");
      return;
    }

    if (!visitorName || !visitorEmail) {
      toast("Please enter your name and email in the chat tab first");
      return;
    }

    setIsSubmitting(true);
    try {
      // Get the default status for this company
      const { data: defaultStatus, error: statusError } = await supabase
        .from("company_statuses")
        .select("id, name")
        .eq("company_id", companyId)
        .order("position", { ascending: true })
        .limit(1)
        .single();

      if (statusError) {
        console.error("Error fetching default status:", statusError);
      }

      const { error } = await supabase.from("feedback").insert({
        title: title.trim(),
        description: description.trim(),
        status: defaultStatus?.name || null,
        created_by: null,
        submitter_name: visitorName,
        submitter_email: visitorEmail,
        company_id: companyId,
        board_id: selectedBoardId || null,
      });

      if (error) throw error;

      setIsSubmitted(true);
      setTitle("");
      setDescription("");
      toast("Feedback submitted successfully!");

      // Reset the submitted state after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      toast("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingBoards) {
    return (
      <div className="flex-1 p-3 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="flex-1 p-6 flex flex-col items-center justify-center text-center">
        <div className="bg-green-50 dark:bg-green-950 rounded-full p-4 mb-4">
          <CheckCircle className="h-12 w-12 text-green-500" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Thank you!</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
          Your feedback has been submitted successfully. We appreciate you taking the time to share your thoughts.
        </p>
        <Button
          onClick={() => setIsSubmitted(false)}
          className="rounded-xl h-11"
          style={primaryColor ? {
            backgroundColor: primaryColor,
            color: textColor
          } : {}}
        >
          Submit Another
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full p-5 overflow-y-auto">
      <div className="space-y-5 pb-4">
        <div className="flex items-center gap-2.5">
          <MessageSquarePlus className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">Submit Feedback</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {boards.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Board</label>
              <Select value={selectedBoardId} onValueChange={setSelectedBoardId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a board" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      <div className="flex items-center space-x-2">
                        <span>{board.emoji}</span>
                        <span>{board.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Title *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of your feedback"
              className="h-11 px-4 rounded-xl text-base"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Description *</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide detailed feedback..."
              className="min-h-[120px] resize-none rounded-xl px-4 py-3 text-base"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
            Submitting as: <span className="font-medium">{visitorName}</span> ({visitorEmail})
          </div>

          <Button
            type="submit"
            className="w-full h-11 rounded-xl font-medium"
            disabled={isSubmitting || !title.trim() || !description.trim()}
            style={primaryColor ? {
              backgroundColor: primaryColor,
              color: textColor
            } : {}}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              "Submit Feedback"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default FeedbackTab;
